import type { AuthTelegramResponse, EventsListResponse, GroupsListResponse, UsersListResponse } from '../../api/types';
import type { BootstrapRepository } from '../../repositories/ApiBootstrapRepository';
import { setTelegramInitData } from '../../telegram/telegram';
import { addDays, formatDateYYYYMMDD } from '../../utils/date';

export type BootstrapResult = {
  session: AuthTelegramResponse;
  events: EventsListResponse;
  groups: GroupsListResponse;
  users: UsersListResponse;
  range: { from: string; to: string };
};

/** Thrown outside Telegram when there is no valid auth cookie: the user has to sign in via the browser flow. */
export class BrowserLoginRequiredError extends Error {
  constructor() {
    super('Требуется вход через Telegram');
    this.name = 'BrowserLoginRequiredError';
  }
}

export class BootstrapService {
  constructor(
    private readonly repo: BootstrapRepository,
    private readonly deps: {
      getTelegramInitData: () => string | null;
      now?: () => Date;
    },
  ) {}

  async bootstrap(): Promise<BootstrapResult> {
    const initData = (this.deps.getTelegramInitData() ?? '').trim();

    let session: AuthTelegramResponse;
    if (initData) {
      // Seed the cache so auto-refresh can reuse the same initData.
      setTelegramInitData(initData);
      session = await this.repo.authTelegram(initData);
    } else {
      // Regular browser: no initData, so try to resume the session from the auth cookie.
      try {
        session = await this.repo.getMe();
      } catch {
        throw new BrowserLoginRequiredError();
      }
    }

    const now = this.deps.now?.() ?? new Date();
    const from = formatDateYYYYMMDD(now);
    const to = formatDateYYYYMMDD(addDays(now, 6));

    const [events, groups, users] = await Promise.all([
      this.repo.listEventsRange({ from, to }),
      this.repo.listGroups(),
      this.repo.listUsers(),
    ]);

    return {
      session,
      events,
      groups,
      users,
      range: { from, to },
    };
  }
}
